type GatePanelKey = "brand" | "franchise";

type MainSpaceStoryGsap = {
    registerPlugin: (...plugins: unknown[]) => void;
    set: (target: unknown, vars: Record<string, unknown>) => void;
};

type MainSpaceStoryScrollTrigger = {
    create: (vars: Record<string, unknown>) => unknown;
    refresh?: () => void;
    update?: () => void;
};

type MainVisualSwiperInstance = {
    activeIndex?: number;
    realIndex?: number;
    on?: (eventName: string, callback: () => void) => void;
    slideTo?: (index: number) => void;
    slideToLoop?: (index: number) => void;
};

const GATE_STAGE_SELECTOR = "[data-gate-stage]";
const GATE_PANEL_SELECTOR = "[data-gate-panel]";
const SEOUL_CLOCK_SELECTOR = "[data-gate-seoul-clock]";
const MAIN_VISUAL_SELECTOR = "[data-main-visual]";
const MAIN_VISUAL_SLIDER_SELECTOR = "[data-main-visual-slider]";
const MAIN_SPACE_STORY_SELECTOR = "[data-main-space-story]";
const MAIN_SPACE_STORY_PIN_SELECTOR = "[data-main-space-pin]";
const MAIN_WELCOME_SCROLL_SELECTOR = "[data-main-welcome-scroll]";
const MAIN_BEST_MENU_SELECTOR = "[data-main-best-menu]";
const MAIN_BEST_MENU_SLIDER_SELECTOR = "[data-main-best-menu-slider]";

export const PichiHomeController = {
    index(): void {
        initSeoulClock();
        initGatePanelState();
    },
    main(): void {
        initMainVisualSwiper();
        initMainSpaceStory();
        initMainWelcomeScroll();
        initMainBestMenuSwiper();
    },
};

function initSeoulClock(): void {
    const clock = document.querySelector<HTMLElement>(SEOUL_CLOCK_SELECTOR);
    if (!clock) return;

    const formatSeoulTime = (): string => {
        return new Date().toLocaleTimeString("en-GB", {
            timeZone: "Asia/Seoul",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        });
    };

    const render = (): void => {
        const time = formatSeoulTime();
        clock.textContent = time;
        clock.setAttribute("datetime", time);
    };

    render();
    window.setInterval(render, 1000);
}

function initGatePanelState(): void {
    const stage = document.querySelector<HTMLElement>(GATE_STAGE_SELECTOR);
    if (!stage) return;

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const panels = Array.from(stage.querySelectorAll<HTMLElement>(GATE_PANEL_SELECTOR));

    const readPanelKey = (panel: HTMLElement): GatePanelKey | null => {
        const key = panel.dataset.gatePanel;
        return key === "brand" || key === "franchise" ? key : null;
    };

    const setActivePanel = (panel: HTMLElement): void => {
        if (!finePointer.matches) return;
        const key = readPanelKey(panel);
        if (key) {
            stage.dataset.gateActivePanel = key;
        }
    };

    const clearActivePanel = (): void => {
        delete stage.dataset.gateActivePanel;
    };

    panels.forEach((panel) => {
        panel.addEventListener("pointerenter", () => setActivePanel(panel));
        panel.addEventListener("pointerleave", clearActivePanel);
        panel.addEventListener("focus", () => setActivePanel(panel));
        panel.addEventListener("blur", () => {
            window.setTimeout(() => {
                if (!stage.contains(document.activeElement)) {
                    clearActivePanel();
                }
            }, 0);
        });
    });

    const clearWhenTouchLike = (): void => {
        if (!finePointer.matches) {
            clearActivePanel();
        }
    };

    if ("addEventListener" in finePointer) {
        finePointer.addEventListener("change", clearWhenTouchLike);
    } else {
        finePointer.addListener(clearWhenTouchLike);
    }
}

function initMainVisualSwiper(): void {
    const root = document.querySelector<HTMLElement>(MAIN_VISUAL_SELECTOR);
    if (!root) return;

    const slider = root.querySelector<HTMLElement>(MAIN_VISUAL_SLIDER_SELECTOR);
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll<HTMLElement>(".swiper-slide"));
    const paginationItems = Array.from(
        root.querySelectorAll<HTMLButtonElement>("[data-main-visual-pagination-item]")
    );
    const prevEl = root.querySelector<HTMLElement>("[data-main-visual-prev]");
    const nextEl = root.querySelector<HTMLElement>("[data-main-visual-next]");
    const Swiper = (window as unknown as {
        Swiper?: new (element: HTMLElement, options: Record<string, unknown>) => MainVisualSwiperInstance;
    }).Swiper;

    if (!Swiper || slides.length === 0) return;

    const swiper = new Swiper(slider, {
        loop: slides.length > 1,
        effect: "fade",
        fadeEffect: {
            crossFade: true,
        },
        speed: 780,
        allowTouchMove: slides.length > 1,
        navigation: {
            prevEl,
            nextEl,
        },
    });

    const updatePagination = (): void => {
        if (paginationItems.length === 0) return;

        const rawIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;
        const activeIndex = ((rawIndex % paginationItems.length) + paginationItems.length) % paginationItems.length;

        paginationItems.forEach((item, index) => {
            const isActive = index === activeIndex;
            item.classList.toggle("is-active", isActive);
            if (isActive) {
                item.setAttribute("aria-current", "true");
            } else {
                item.removeAttribute("aria-current");
            }
        });
    };

    paginationItems.forEach((item) => {
        item.addEventListener("click", () => {
            const index = Number(item.dataset.mainVisualIndex ?? 0);
            if (Number.isNaN(index)) return;

            if (swiper.slideToLoop) {
                swiper.slideToLoop(index);
            } else if (swiper.slideTo) {
                swiper.slideTo(index);
            }
        });
    });

    swiper.on?.("slideChange", updatePagination);
    swiper.on?.("realIndexChange", updatePagination);
    updatePagination();
}

function initMainWelcomeScroll(): void {
    const trigger = document.querySelector<HTMLAnchorElement>(MAIN_WELCOME_SCROLL_SELECTOR);
    if (!trigger) return;

    trigger.addEventListener("click", (event) => {
        const targetSelector = trigger.getAttribute("href");
        if (!targetSelector || !targetSelector.startsWith("#")) return;

        const target = document.querySelector<HTMLElement>(targetSelector);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    });
}

function initMainSpaceStory(): void {
    const root = document.querySelector<HTMLElement>(MAIN_SPACE_STORY_SELECTOR);
    if (!root) return;

    if (!root.querySelector<HTMLElement>(MAIN_SPACE_STORY_PIN_SELECTOR)) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const globals = window as unknown as {
        gsap?: MainSpaceStoryGsap;
        ScrollTrigger?: MainSpaceStoryScrollTrigger;
        lenis?: {
            on?: (eventName: "scroll", callback: () => void) => void;
        };
    };

    if (prefersReducedMotion || !globals.gsap || !globals.ScrollTrigger?.create) {
        root.dataset.mainSpaceStoryStatic = "true";
        return;
    }

    const gsap = globals.gsap;
    const scrollTrigger = globals.ScrollTrigger;
    gsap.registerPlugin(scrollTrigger);

    const scenes = Array.from(root.querySelectorAll<HTMLElement>("[data-main-space-scene]"));
    const scene = (key: string): HTMLElement | null =>
        root.querySelector<HTMLElement>(`[data-main-space-scene="${key}"]`);
    const floatAssets = Array.from(root.querySelectorAll<HTMLElement>("[data-main-space-float]"));
    const galaxy = root.querySelector<HTMLElement>("[data-main-space-galaxy]");
    const galaxyBig = root.querySelector<HTMLElement>("[data-main-space-galaxy-big]");
    const earth = root.querySelector<HTMLElement>("[data-main-space-earth]");
    const centeredAssets = [galaxy, galaxyBig].filter((element): element is HTMLElement => Boolean(element));
    const orderedFloatAssets = [...floatAssets].sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        return aRect.top - bRect.top || aRect.left - bRect.left;
    });
    const floatingDropGroups = [
        { startIndex: 0, endIndex: Math.min(3, orderedFloatAssets.length), start: 1.08 },
        { startIndex: 3, endIndex: Math.min(6, orderedFloatAssets.length), start: 2.08 },
        { startIndex: 6, endIndex: orderedFloatAssets.length, start: 3.08 },
    ].filter((group) => group.startIndex < group.endIndex);
    const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
    const smoothStep = (value: number): number => {
        const progress = clamp01(value);
        return progress * progress * (3 - 2 * progress);
    };

    const setFloatingAssetsFromProgress = (progress: number): void => {
        const storyProgress = clamp01(progress) * 6;
        const appearProgress = smoothStep(storyProgress);
        const dropY = Math.max(260, window.innerHeight * 0.46);

        orderedFloatAssets.forEach((element, index) => {
            const group = floatingDropGroups.find(
                (candidate) => index >= candidate.startIndex && index < candidate.endIndex,
            );
            const localIndex = group ? index - group.startIndex : 0;
            const dropProgress = group
                ? smoothStep((storyProgress - group.start - localIndex * 0.12) / 0.56)
                : 0;
            const opacity = appearProgress * (1 - dropProgress);

            element.style.opacity = opacity <= 0.001 ? "0" : opacity.toFixed(3);
            element.style.scale = appearProgress <= 0.001 ? "0" : appearProgress.toFixed(3);
            element.style.translate = `0 ${Math.round(dropY * dropProgress)}px`;
        });
    };

    gsap.set(scenes, { opacity: 0, y: 18 });
    gsap.set(scene("1"), { opacity: 1, y: 0 });
    gsap.set(centeredAssets, { opacity: 0, scale: 0.86, xPercent: -50, yPercent: -50 });
    gsap.set(earth, { opacity: 0, scale: 0.72, xPercent: -50, yPercent: -50 });

    let activeStep = -1;
    const setStep = (step: number): void => {
        if (step === activeStep) return;
        activeStep = step;
        root.dataset.mainSpaceStoryStep = String(step + 1);

        gsap.set(scenes, { opacity: 0, y: 16 });
        gsap.set(scene(String(step + 1)), { opacity: 1, y: 0 });

        gsap.set(galaxy, {
            opacity: step === 2 ? 0.82 : 0,
            scale: step === 2 ? 1 : 0.86,
            xPercent: -50,
            yPercent: -50,
        });
        gsap.set(galaxyBig, {
            opacity: step === 3 ? 0.68 : 0,
            scale: step === 3 ? 1 : 0.86,
            xPercent: -50,
            yPercent: -50,
        });
        gsap.set(earth, {
            opacity: step === 4 ? 0.28 : step === 5 ? 1 : 0,
            scale: step === 4 ? 0.86 : step === 5 ? 1.12 : 0.72,
            xPercent: -50,
            yPercent: -50,
        });
    };

    const setStateFromProgress = (progress: number): void => {
        const nextStep = Math.min(5, Math.max(0, Math.floor(progress * 6)));
        setStep(nextStep);
        setFloatingAssetsFromProgress(progress);
    };

    const readScrollProgress = (): number => {
        const distance = Math.max(1, root.offsetHeight - window.innerHeight);
        if (distance <= 0) return 0;
        return Math.min(1, Math.max(0, (window.scrollY - root.offsetTop) / distance));
    };

    const updateFromWindowScroll = (): void => {
        setStateFromProgress(readScrollProgress());
    };

    setStateFromProgress(readScrollProgress());

    scrollTrigger.create({
        trigger: root,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self: { progress: number }) => setStateFromProgress(self.progress),
    });

    globals.lenis?.on?.("scroll", () => {
        scrollTrigger.update?.();
        updateFromWindowScroll();
    });
    window.addEventListener("scroll", updateFromWindowScroll, { passive: true });
    window.addEventListener("resize", updateFromWindowScroll);
    window.requestAnimationFrame(() => {
        scrollTrigger.refresh?.();
        updateFromWindowScroll();
    });
}

function initMainBestMenuSwiper(): void {
    const root = document.querySelector<HTMLElement>(MAIN_BEST_MENU_SELECTOR);
    if (!root) return;

    const slider = root.querySelector<HTMLElement>(MAIN_BEST_MENU_SLIDER_SELECTOR);
    if (!slider) return;

    const Swiper = (window as unknown as {
        Swiper?: new (element: HTMLElement, options: Record<string, unknown>) => unknown;
    }).Swiper;
    if (!Swiper) return;

    const slides = Array.from(slider.querySelectorAll<HTMLElement>(".swiper-slide"));
    const prevEl = root.querySelector<HTMLElement>("[data-main-menu-prev]");
    const nextEl = root.querySelector<HTMLElement>("[data-main-menu-next]");

    new Swiper(slider, {
        loop: slides.length > 3,
        centeredSlides: true,
        slidesPerView: "auto",
        spaceBetween: 28,
        speed: 700,
        grabCursor: true,
        slideToClickedSlide: true,
        navigation: {
            prevEl,
            nextEl,
        },
        breakpoints: {
            768: {
                spaceBetween: 34,
            },
            1024: {
                spaceBetween: 44,
            },
            1440: {
                spaceBetween: 52,
            },
        },
    });
}
